import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { Button } from 'components/buttons';
import { ButtonWithLoading } from 'components/buttons/buttonWithLoading';
import { Panel } from 'components/containers';
import { FormField } from 'components/form/formField/formField';
import IconCopy from 'components/icons/copy';
import IconCross from 'components/icons/cross';
import { Input } from 'components/inputs';
import { MultilineInput } from 'components/inputs/input/multilineInput';
import Modal from 'components/modal/modal';

import { CreateApiKeyResponse } from 'types/apiKeys';

import { LABELS } from '../apiKeys.keys';
import { ApiKeyTableRow } from '../types';
import { parseWhitelistInput } from '../utils';
import {
  Buttons,
  Delimeter,
  FieldSection,
  Hint,
  Label,
  ModalControls,
  ModalRoot,
  PanelContent,
  RevealRow,
  RevealValue,
  Warning,
} from './apiKeyModal.styles';

export interface ApiKeyFormPayload {
  name: string;
  whitelistedIps: string[];
}

interface ApiKeyModalProps {
  open: boolean;
  onClose: () => void;
  editingKey: ApiKeyTableRow | null;
  onCreate: (payload: ApiKeyFormPayload) => Promise<{ success: boolean; createdKey?: CreateApiKeyResponse }>;
  onUpdate: (apiKeyUuid: string, payload: ApiKeyFormPayload) => Promise<boolean>;
  isSubmitting: boolean;
  error?: Record<string, string>;
  reset: () => void;
}

export const ApiKeyModal = ({ open, onClose, editingKey, onCreate, onUpdate, isSubmitting, error, reset }: ApiKeyModalProps) => {
  const { t } = useTranslation();
  const isEditMode = Boolean(editingKey);
  const [name, setName] = useState('');
  const [whitelistInput, setWhitelistInput] = useState('');
  const [createdKey, setCreatedKey] = useState<CreateApiKeyResponse | null>(null);
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    if (!open) {
      return;
    }

    setName(editingKey?.name || '');
    setWhitelistInput(editingKey ? editingKey.whitelistedIps.join(', ') : '');
    setCreatedKey(null);
    setCopied(false);
  }, [open, editingKey]);

  const closeModal = () => {
    if (isSubmitting) {
      return;
    }

    reset();
    onClose();
  };

  const handleSubmit = async () => {
    if (isSubmitting || !name) {
      return;
    }

    const whitelistedIps = parseWhitelistInput(whitelistInput);

    if (isEditMode && editingKey) {
      const success = await onUpdate(editingKey.uuid, { name, whitelistedIps });
      if (success) {
        closeModal();
      }
      return;
    }

    const response = await onCreate({ name, whitelistedIps });
    if (response.success && response.createdKey) {
      setCreatedKey(response.createdKey);
    }
  };

  const handleCopySecret = async () => {
    if (!createdKey) {
      return;
    }

    await navigator.clipboard.writeText(createdKey.secretKey);
    setCopied(true);
  };

  const canSubmit = Boolean(name) && !isSubmitting;

  if (createdKey) {
    return (
      <Modal open={open} onClose={onClose}>
        <ModalRoot>
          <Panel label={t(LABELS.MODAL.REVEAL.TITLE)}>
            <PanelContent>
              <Warning>{t(LABELS.MODAL.REVEAL.WARNING)}</Warning>
              <FieldSection>
                <Label>{t(LABELS.MODAL.REVEAL.SECRET_KEY)}</Label>
                <RevealRow>
                  <RevealValue>{createdKey.secretKey}</RevealValue>
                  <Button flat onClick={handleCopySecret}>
                    <IconCopy />
                  </Button>
                </RevealRow>
                {copied && <Hint>{t(LABELS.MODAL.REVEAL.COPIED)}</Hint>}
              </FieldSection>
              <Delimeter />
              <Buttons>
                <Button primary onClick={onClose}>
                  {t(LABELS.MODAL.BUTTONS.DONE)}
                </Button>
              </Buttons>
            </PanelContent>
          </Panel>
        </ModalRoot>
      </Modal>
    );
  }

  return (
    <Modal open={open} onClose={closeModal}>
      <ModalRoot>
        <Panel
          label={t(isEditMode ? LABELS.MODAL.EDIT_TITLE : LABELS.MODAL.CREATE_TITLE)}
          controls={
            <ModalControls>
              <Button flat onClick={closeModal}>
                <IconCross />
              </Button>
            </ModalControls>
          }
        >
          <PanelContent>
            <FieldSection>
              <Label>{t(LABELS.MODAL.NAME.TITLE)}</Label>
              <FormField error={error?.name}>
                <Input placeholder={t(LABELS.MODAL.NAME.PLACEHOLDER)} value={name} onChange={setName} disabled={isSubmitting} fullWidth />
              </FormField>
            </FieldSection>
            <FieldSection>
              <Label>{t(LABELS.MODAL.WHITELIST.TITLE)}</Label>
              <FormField error={error?.whitelistedIps}>
                <MultilineInput
                  placeholder={t(LABELS.MODAL.WHITELIST.PLACEHOLDER)}
                  value={whitelistInput}
                  onChange={setWhitelistInput}
                  disabled={isSubmitting}
                  rows={3}
                  fullWidth
                />
              </FormField>
              <Hint>{t(LABELS.MODAL.WHITELIST.HINT)}</Hint>
            </FieldSection>
            <Delimeter />
            <Buttons>
              <Button secondary onClick={closeModal}>
                {t(LABELS.MODAL.BUTTONS.CANCEL)}
              </Button>
              <ButtonWithLoading primary onClick={handleSubmit} disabled={!canSubmit} isLoading={isSubmitting}>
                {t(LABELS.MODAL.BUTTONS.CONFIRM)}
              </ButtonWithLoading>
            </Buttons>
          </PanelContent>
        </Panel>
      </ModalRoot>
    </Modal>
  );
};
